/**
 * Formatting helpers. Every date and time in this app renders in Asia/Dubai,
 * regardless of where the device thinks it is. Money is AED.
 *
 * Copy rule: no em dashes anywhere in user-facing strings, plain hyphens only.
 */

export const DUBAI_TZ = 'Asia/Dubai'
export const CURRENCY = 'AED'

function fmt(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat('en-GB', { timeZone: DUBAI_TZ, ...options })
}

const dayFmt = fmt({ weekday: 'short', day: 'numeric', month: 'short' })
const dayLongFmt = fmt({ weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
const timeFmt = fmt({ hour: '2-digit', minute: '2-digit', hour12: false })
const monthFmt = fmt({ month: 'long', year: 'numeric' })

export function toDate(value: string | Date): Date {
  return value instanceof Date ? value : new Date(value)
}

/** "Tue, 26 Aug" */
export function formatDay(value: string | Date): string {
  return dayFmt.format(toDate(value))
}

/** "Tuesday, 26 August 2026" */
export function formatDayLong(value: string | Date): string {
  return dayLongFmt.format(toDate(value))
}

/** "16:30" */
export function formatTime(value: string | Date): string {
  return timeFmt.format(toDate(value))
}

/** "Tue, 26 Aug at 16:30" */
export function formatDateTime(value: string | Date): string {
  return `${formatDay(value)} at ${formatTime(value)}`
}

/** "August 2026" from a period_month date string. */
export function formatMonth(value: string | Date): string {
  return monthFmt.format(toDate(value))
}

/** "2h 30m", "45m", "5h" */
export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

/** "AED 250.00" */
export function formatMoney(amount: number | string | null | undefined): string {
  const n = typeof amount === 'string' ? Number(amount) : (amount ?? 0)
  return `${CURRENCY} ${n.toLocaleString('en-AE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

/** "AED 250" when the fractional part is zero, used for compact totals. */
export function formatMoneyCompact(amount: number | string | null | undefined): string {
  const n = typeof amount === 'string' ? Number(amount) : (amount ?? 0)
  const hasCents = Math.round(n * 100) % 100 !== 0
  return `${CURRENCY} ${n.toLocaleString('en-AE', {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  })}`
}

/** "in 3 days", "in 4 hours", "2 days ago". Rounded, deliberately coarse. */
export function relativeTime(value: string | Date, now: Date = new Date()): string {
  const target = toDate(value)
  const diffMs = target.getTime() - now.getTime()
  const abs = Math.abs(diffMs)
  const minute = 60_000
  const hour = 60 * minute
  const day = 24 * hour

  if (abs < 45 * minute) {
    const mins = Math.max(1, Math.round(abs / minute))
    return diffMs >= 0 ? `in ${mins} min` : `${mins} min ago`
  }
  if (abs < 36 * hour) {
    const hours = Math.round(abs / hour)
    const unit = hours === 1 ? 'hour' : 'hours'
    return diffMs >= 0 ? `in ${hours} ${unit}` : `${hours} ${unit} ago`
  }
  const days = Math.round(abs / day)
  const unit = days === 1 ? 'day' : 'days'
  return diffMs >= 0 ? `in ${days} ${unit}` : `${days} ${unit} ago`
}

/** First day of the Asia/Dubai month for a timestamp, as YYYY-MM-DD. */
export function periodMonth(value: string | Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: DUBAI_TZ,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(toDate(value))
  const year = parts.find((p) => p.type === 'year')?.value ?? '1970'
  const month = parts.find((p) => p.type === 'month')?.value ?? '01'
  return `${year}-${month}-01`
}

/** Turn a snake_case enum value into "Title case" for display. */
export function humanize(value: string | null | undefined): string {
  if (!value) return ''
  const s = value.replace(/_/g, ' ')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Initials for the avatar circle. */
export function initials(fullName: string | null | undefined): string {
  if (!fullName) return '?'
  const parts = fullName.trim().split(/\s+/)
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : ''
  return (first + last).toUpperCase() || '?'
}

export function pluralize(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : (plural ?? `${singular}s`)
}
